import React, { useState, useEffect } from "react";
import cn from "classnames";
import { Link } from "react-router-dom";
import { EditorState, ContentState, convertToRaw, convertFromRaw } from 'draft-js';
import styles from "./NameAndDescription.module.sass";
import Card from "../../../components/Card";
import Icon from "../../../components/Icon";
import TextInput from "../../../components/TextInput";
import Editor from "../../../components/Editor";
import Dropdown from "../../../components/Dropdown";

const DUREE_CONTRAT_OPTIONS = [
    { value: '6', title: '6 mois' },
    { value: '12', title: '1 an' },
    { value: '18', title: '1 an et 6 mois' },
    { value: '24', title: '2 ans' },
    { value: '36', title: '3 ans' },
];

const RYTHME_OPTIONS = [
    { value: '1_semaine_1_semaine', title: '1 semaine école / 1 semaine entreprise' },
    { value: '2_semaines_2_semaines', title: '2 semaines école / 2 semaines entreprise' },
    { value: '1_jour_4_jours', title: '1 jour école / 4 jours entreprise' },
    { value: '2_jours_3_jours', title: '2 jours école / 3 jours entreprise' },
    { value: '3_jours_2_jours', title: '3 jours école / 2 jours entreprise' },
];

const TYPE_CONTRAT_OPTIONS = [
    { value: 'Apprentissage', title: 'Contrat d\'apprentissage' },
    { value: 'Professionnalisation', title: 'Contrat de professionnalisation' },
];

const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
};

const NameAndDescription = ({ className, offer, onOfferChange, onDropdownChange, editorSyncRef }) => {
    // Utiliser useRef pour stocker l'EditorState et éviter les re-rendus inutiles
    const editorStateRef = React.useRef(null);
    
    // Initialiser l'EditorState une seule fois
    const [content, setContent] = useState(() => {
        const description = typeof offer?.description === 'string' ? offer.description : '';
        const editorState = EditorState.createWithContent(ContentState.createFromText(description));
        editorStateRef.current = editorState;
        return editorState;
    });

    // Mettre à jour le contenu de la description quand l'offre change, mais seulement si nécessaire
    // et en préservant la sélection et le focus
    useEffect(() => {
        if (offer?.description && editorStateRef.current) {
            const currentContent = editorStateRef.current.getCurrentContent().getPlainText();
            const newDescription = typeof offer?.description === 'string' ? offer.description : '';
            
            // Ne mettre à jour que si le contenu a réellement changé et est différent
            // de ce que l'utilisateur est en train d'éditer
            if (newDescription !== currentContent && !editorStateRef.current.getSelection().hasFocus) {
                const newState = EditorState.createWithContent(ContentState.createFromText(newDescription));
                editorStateRef.current = newState;
                setContent(newState);
            }
        }
    }, [offer?.description]);

    // Fonction personnalisée pour mettre à jour le contenu de l'éditeur sans mise à jour automatique
    const handleEditorChange = (newEditorState) => {
        // Stocker la référence de l'état de l'éditeur
        editorStateRef.current = newEditorState;
        setContent(newEditorState);
        // Pas de mise à jour automatique des données de l'offre
    };
    
    // Fonction pour synchroniser la description avec l'offre avant la soumission
    const syncDescriptionBeforeSubmit = () => {
        if (editorStateRef.current) {
            const description = editorStateRef.current.getCurrentContent().getPlainText();
            
            if (description !== offer?.description) {
                if (onOfferChange) {
                    onOfferChange({
                        target: {
                            name: 'description',
                            value: description
                        }
                    });
                }
            }
            
            // Retourner la description pour utilisation directe
            return description;
        }
        return offer?.description || '';
    };
    
    // Exposer la fonction de synchronisation au composant parent via la ref
    React.useEffect(() => {
        if (editorSyncRef) {
            editorSyncRef.current = syncDescriptionBeforeSubmit;
        }
    }, [editorSyncRef]);

    // Garder aussi l'écouteur de formulaire pour les cas où il y aurait un formulaire
    React.useEffect(() => {
        const form = document.querySelector('form');
        if (form) {
            const handleSubmit = (e) => {
                syncDescriptionBeforeSubmit();
            };
            form.addEventListener('submit', handleSubmit, true);
            return () => {
                form.removeEventListener('submit', handleSubmit, true);
            };
        }
    }, []);

    const handleDropdownChange = (name, value) => {
        if (onDropdownChange) {
            onDropdownChange(name, value);
        }
    };

    return (
        <Card
            className={cn(styles.card, className)}
            title="Créer une offre d'alternance"
            classTitle="title-green"
        >
            <div className={styles.description}>
                <TextInput
                    className={styles.field}
                    label="Titre du poste"
                    name="title"
                    type="text"
                    placeholder="Ex: Développeur web en alternance"
                    value={offer?.title || ''}
                    onChange={onOfferChange}
                    required
                />
                
                <Editor
                    state={content}
                    onChange={handleEditorChange}
                    classEditor={styles.editor}
                    label="Description de la mission"
                    tooltip="Description détaillée de la mission et des responsabilités"
                    placeholder="Décrivez la mission, les responsabilités et les objectifs..."
                />

                <div className={styles.group}>
                    <TextInput
                        className={styles.field}
                        label="Lieu de travail"
                        name="workplace_location"
                        type="text"
                        placeholder="Ex: Paris 15ème, télétravail possible"
                        value={offer?.workplace_location || ''}
                        onChange={onOfferChange}
                        required
                    />
                    
                    <TextInput
                        className={styles.field}
                        label="Date de début"
                        name="start_date"
                        type="date"
                        value={formatDateForInput(offer?.start_date)}
                        onChange={onOfferChange}
                    />
                </div>

                <div className={styles.group}>
                    <div className={styles.field}>
                        <Dropdown
                            className={styles.dropdown}
                            label="Durée du contrat"
                            name="contract_duration"
                            options={DUREE_CONTRAT_OPTIONS}
                            setValue={(option) => handleDropdownChange('contract_duration', option.value)}
                            value={DUREE_CONTRAT_OPTIONS.find(opt => opt.value === offer?.contract_duration) || ''}
                        />
                    </div>

                    <div className={styles.field}>
                        <Dropdown
                            className={styles.dropdown}
                            label="Rythme souhaité"
                            name="work_rhythm"
                            options={RYTHME_OPTIONS}
                            setValue={(option) => handleDropdownChange('work_rhythm', option.value)}
                            value={RYTHME_OPTIONS.find(opt => opt.value === offer?.work_rhythm) || ''}
                        />
                    </div>
                </div>

                <div className={styles.group}>
                    <div className={styles.field}>
                        <Dropdown
                            className={styles.dropdown}
                            label="Type de contrat"
                            name="contract_type"
                            options={TYPE_CONTRAT_OPTIONS}
                            setValue={(option) => handleDropdownChange('contract_type', option.value)}
                            value={TYPE_CONTRAT_OPTIONS.find(opt => opt.value === offer?.contract_type) || ''}
                        />
                    </div>

                    <TextInput
                        className={styles.field}
                        label="Secteur d'activité"
                        name="activity_sector"
                        type="text"
                        placeholder="Ex: Informatique, Commerce, Industrie..."
                        value={offer?.activity_sector || ''}
                        onChange={onOfferChange}
                    />
                </div>
            </div>
        </Card>
    );
};

export default NameAndDescription;
